import { FuelType } from '@rfleet/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUp, Paperclip, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, Field, Spinner, useToast } from '../../components/ui';
import { api, type Paginated } from '../../lib/api';
import { uploadFile } from '../../lib/upload';
import type { DriverRow } from '../drivers/DriversPage';
import type { VehicleRow } from '../vehicles/VehiclesPage';
import { catName, type ExpenseCategory, type ExpenseDetail } from './types';

interface FormState {
  categoryId: string;
  amountEgp: string;
  occurredAt: string;
  vehicleId: string;
  driverId: string;
  tripId: string;
  locationText: string;
  notes: string;
  isRebillable: boolean;
  receiptFileId: string;
  receiptName: string;
  liters: string;
  pricePerLiter: string;
  fuelType: string;
  odometerKm: string;
  isFullTank: boolean;
}

const toLocal = (iso?: string | null) => {
  const d = iso ? new Date(iso) : new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60_000).toISOString().slice(0, 16);
};

const empty = (prefill?: Partial<FormState>): FormState => ({
  categoryId: '',
  amountEgp: '',
  occurredAt: toLocal(),
  vehicleId: '',
  driverId: '',
  tripId: '',
  locationText: '',
  notes: '',
  isRebillable: false,
  receiptFileId: '',
  receiptName: '',
  liters: '',
  pricePerLiter: '',
  fuelType: '',
  odometerKm: '',
  isFullTank: true,
  ...prefill,
});

/** Create/edit dialog shared by the list, the detail page and the trip page (prefilled vehicle/driver/trip). */
export function ExpenseFormDialog({ open, onClose, editing, prefill, onSaved }: { open: boolean; onClose: () => void; editing?: ExpenseDetail | null; prefill?: { vehicleId?: string; driverId?: string; tripId?: string }; onSaved?: (id: string) => void }) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [f, setF] = useState<FormState>(empty());
  const [uploading, setUploading] = useState(false);

  const categories = useQuery({ queryKey: ['expense-categories'], queryFn: () => api<ExpenseCategory[]>('/expenses/categories') });
  const vehicles = useQuery({ queryKey: ['vehicles-lite'], queryFn: () => api<Paginated<VehicleRow>>('/vehicles?pageSize=200&status=ACTIVE').then((r) => r.data) });
  const drivers = useQuery({ queryKey: ['drivers-lite'], queryFn: () => api<Paginated<DriverRow>>('/drivers?pageSize=200&status=ACTIVE').then((r) => r.data) });

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setF(
        empty({
          categoryId: editing.category.id,
          amountEgp: String(editing.amountEgp),
          occurredAt: toLocal(editing.occurredAt),
          vehicleId: editing.vehicle?.id ?? '',
          driverId: editing.driver?.id ?? '',
          tripId: editing.trip?.id ?? '',
          locationText: editing.locationText ?? '',
          notes: editing.notes ?? '',
          isRebillable: editing.isRebillable,
          receiptFileId: editing.receiptFileId ?? '',
          receiptName: editing.receiptFileId ? t('expenses.receiptAttached') : '',
          liters: editing.fuelLog ? String(editing.fuelLog.liters) : '',
          pricePerLiter: editing.fuelLog ? String(editing.fuelLog.pricePerLiter) : '',
          fuelType: editing.fuelLog?.fuelType ?? '',
          odometerKm: editing.fuelLog?.odometerKm != null ? String(editing.fuelLog.odometerKm) : '',
          isFullTank: editing.fuelLog?.isFullTank ?? true,
        }),
      );
    } else {
      setF(empty({ vehicleId: prefill?.vehicleId ?? '', driverId: prefill?.driverId ?? '', tripId: prefill?.tripId ?? '' }));
    }
  }, [open, editing, prefill?.vehicleId, prefill?.driverId, prefill?.tripId, t]);

  const category = useMemo(() => categories.data?.find((c) => c.id === f.categoryId), [categories.data, f.categoryId]);
  const isFuel = !!category?.isFuel;
  const vehicle = vehicles.data?.find((v) => v.id === f.vehicleId);

  // liters × price ⇄ amount helpers
  const derivedPrice = f.liters && f.amountEgp && !f.pricePerLiter ? (Number(f.amountEgp) / Number(f.liters)).toFixed(3) : '';

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        categoryId: f.categoryId,
        amountEgp: Number(f.amountEgp),
        occurredAt: new Date(f.occurredAt).toISOString(),
        vehicleId: f.vehicleId || null,
        driverId: f.driverId || null,
        tripId: f.tripId || null,
        locationText: f.locationText || null,
        notes: f.notes || null,
        isRebillable: f.isRebillable,
        receiptFileId: f.receiptFileId || null,
        fuel: isFuel
          ? { liters: Number(f.liters), pricePerLiter: f.pricePerLiter ? Number(f.pricePerLiter) : null, fuelType: f.fuelType || null, odometerKm: f.odometerKm ? Number(f.odometerKm) : null, isFullTank: f.isFullTank }
          : null,
      };
      return editing ? api<ExpenseDetail>(`/expenses/${editing.id}`, { method: 'PATCH', json: body }) : api<ExpenseDetail>('/expenses', { method: 'POST', json: body });
    },
    onSuccess: (e) => {
      toast.success(t('common.saved'));
      void qc.invalidateQueries({ queryKey: ['expenses'] });
      void qc.invalidateQueries({ queryKey: ['expense', e.id] });
      onSaved?.(e.id);
      onClose();
    },
    onError: toast.error,
  });

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const id = await uploadFile(file, 'receipt');
      setF((p) => ({ ...p, receiptFileId: id, receiptName: file.name }));
    } catch (e) {
      toast.error(e);
    } finally {
      setUploading(false);
    }
  };

  const valid = !!f.categoryId && Number(f.amountEgp) > 0 && (!isFuel || (Number(f.liters) > 0 && !!f.vehicleId));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t('expenses.edit') : t('expenses.add')}
      size="lg"
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn-primary" disabled={!valid || save.isPending || uploading} onClick={() => save.mutate()}>
            {save.isPending ? <Spinner /> : null} {t('common.save')}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <span className="label">{t('expenses.category')} <span className="text-danger-fg">*</span></span>
          <div className="flex flex-wrap gap-2" role="radiogroup">
            {(categories.data ?? []).map((c) => (
              <button
                key={c.id}
                type="button"
                role="radio"
                aria-checked={f.categoryId === c.id}
                className={`min-h-9 rounded-full border px-3 text-sm font-semibold transition-colors ${f.categoryId === c.id ? 'border-brand bg-brand text-white' : 'border-border bg-surface text-text-2 hover:bg-surface-hover'}`}
                onClick={() => setF((p) => ({ ...p, categoryId: c.id, fuelType: p.fuelType || (c.isFuel ? (vehicle?.fuelType ?? '') : '') }))}
              >
                {catName(c, i18n.language)}
              </button>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('expenses.amount')} required>
            {(id) => <input id={id} type="number" inputMode="decimal" min={0.01} step={0.01} dir="ltr" className="input text-lg font-bold" value={f.amountEgp} onChange={(e) => setF((p) => ({ ...p, amountEgp: e.target.value }))} />}
          </Field>
          <Field label={t('expenses.occurredAt')} required>
            {(id) => <input id={id} type="datetime-local" dir="ltr" className="input" value={f.occurredAt} onChange={(e) => setF((p) => ({ ...p, occurredAt: e.target.value }))} />}
          </Field>
          <Field label={t('expenses.vehicle')} required={isFuel}>
            {(id) => (
              <select id={id} className="input" value={f.vehicleId} onChange={(e) => setF((p) => ({ ...p, vehicleId: e.target.value, fuelType: p.fuelType || (vehicles.data?.find((v) => v.id === e.target.value)?.fuelType ?? '') }))}>
                <option value="">—</option>
                {(vehicles.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.plateAr}{v.name ? ` · ${v.name}` : ''}</option>)}
              </select>
            )}
          </Field>
          <Field label={t('expenses.driver')}>
            {(id) => (
              <select id={id} className="input" value={f.driverId} onChange={(e) => setF((p) => ({ ...p, driverId: e.target.value }))}>
                <option value="">—</option>
                {(drivers.data ?? []).map((d) => <option key={d.id} value={d.id}>{d.fullName}</option>)}
              </select>
            )}
          </Field>
        </div>

        {isFuel ? (
          <div className="rounded-xl border border-border-soft bg-surface-2 p-3">
            <div className="mb-2 text-sm font-bold text-text-2">{t('expenses.fuelDetails')}</div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Field label={t('expenses.liters')} required>
                {(id) => <input id={id} type="number" inputMode="decimal" min={0.01} step={0.01} dir="ltr" className="input" value={f.liters} onChange={(e) => setF((p) => ({ ...p, liters: e.target.value }))} />}
              </Field>
              <Field label={t('expenses.pricePerLiter')} hint={derivedPrice ? t('expenses.derivedPrice', { price: derivedPrice }) : undefined}>
                {(id) => <input id={id} type="number" inputMode="decimal" min={0} step={0.001} dir="ltr" className="input" placeholder={derivedPrice} value={f.pricePerLiter} onChange={(e) => setF((p) => ({ ...p, pricePerLiter: e.target.value }))} />}
              </Field>
              <Field label={t('expenses.fuelType')}>
                {(id) => (
                  <select id={id} className="input" value={f.fuelType} onChange={(e) => setF((p) => ({ ...p, fuelType: e.target.value }))}>
                    <option value="">{t('expenses.fuelTypeFromVehicle')}</option>
                    {FuelType.map((ft) => <option key={ft} value={ft}>{t(`vehicles.fuelTypes.${ft}`)}</option>)}
                  </select>
                )}
              </Field>
              <Field label={t('expenses.odometerKm')} hint={t('expenses.odometerHint')}>
                {(id) => <input id={id} type="number" inputMode="numeric" min={0} dir="ltr" className="input" value={f.odometerKm} onChange={(e) => setF((p) => ({ ...p, odometerKm: e.target.value }))} />}
              </Field>
            </div>
            <label className="mt-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={f.isFullTank} onChange={(e) => setF((p) => ({ ...p, isFullTank: e.target.checked }))} /> {t('expenses.fullTank')}
            </label>
          </div>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('expenses.location')}>{(id) => <input id={id} className="input" value={f.locationText} onChange={(e) => setF((p) => ({ ...p, locationText: e.target.value }))} />}</Field>
          <Field label={t('expenses.receipt')}>
            {(id) => (
              <div className="flex items-center gap-2">
                <label htmlFor={id} className="btn-secondary cursor-pointer">
                  {uploading ? <Spinner /> : <FileUp size={16} />} {f.receiptFileId ? t('expenses.replaceReceipt') : t('expenses.uploadReceipt')}
                </label>
                <input id={id} type="file" accept="image/*,application/pdf" className="sr-only" onChange={(e) => void onFile(e.target.files?.[0])} />
                {f.receiptFileId ? (
                  <span className="flex items-center gap-1 truncate text-xs text-text-muted">
                    <Paperclip size={12} /> {f.receiptName}
                    <button type="button" className="btn-ghost size-6 !p-0" aria-label={t('common.delete')} onClick={() => setF((p) => ({ ...p, receiptFileId: '', receiptName: '' }))}><X size={12} /></button>
                  </span>
                ) : null}
              </div>
            )}
          </Field>
        </div>
        <Field label={t('expenses.notes')}>{(id) => <textarea id={id} className="input min-h-20" value={f.notes} onChange={(e) => setF((p) => ({ ...p, notes: e.target.value }))} />}</Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.isRebillable} onChange={(e) => setF((p) => ({ ...p, isRebillable: e.target.checked }))} /> {t('expenses.rebillable')}
          <span className="text-xs text-text-faint">— {t('expenses.rebillableHint')}</span>
        </label>
      </div>
    </Dialog>
  );
}
